import { useEffect, useMemo, useState } from "react";
import {
  Users, Wallet, TrendingUp, Gift, Copy, Send, BadgeDollarSign, Trophy, Banknote,
  ArrowUpRight, Sparkles, Star, Share2, Activity, BarChart3, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import { fmtBDT } from "../lib/tier";

function shortDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso.slice(0, 10); }
}

function buildSeries(referrals, days = 14) {
  // Build a daily series of new referrals across the last N days.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const series = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
    series.push({ key, label, signups: 0 });
  }
  (referrals || []).forEach((r) => {
    if (!r.joined_at) return;
    const k = String(r.joined_at).slice(0, 10);
    const row = series.find((s) => s.key === k);
    if (row) row.signups += 1;
  });
  return series;
}

export default function PromoDashboard() {
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [extra, setExtra] = useState(null);
  const [promoStats, setPromoStats] = useState([]);
  const [goal, setGoal] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [cyclePreview, setCyclePreview] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dRes, rRes, xRes, pRes, gRes, lRes, cpRes] = await Promise.all([
        api.get("/aff-panel/dashboard"),
        api.get("/aff-panel/referrals").catch(() => ({ data: { rows: [] } })),
        api.get("/aff-panel/stats").catch(() => ({ data: null })),
        api.get("/aff-panel/promo-codes/stats").catch(() => ({ data: { rows: [] } })),
        api.get("/aff-panel/goal").catch(() => ({ data: null })),
        api.get("/aff-panel/leaderboard-mini").catch(() => ({ data: null })),
        api.get("/aff-panel/cycle-preview").catch(() => ({ data: null })),
      ]);
      setData(dRes.data);
      setReferrals(rRes.data?.rows || []);
      setExtra(xRes.data);
      setPromoStats(pRes.data?.rows || []);
      setGoal(gRes.data);
      setLeaderboard(lRes.data);
      setCyclePreview(cpRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load dashboard");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveGoal = async () => {
    const v = parseFloat(goalInput);
    if (!v || v < 0) return toast.error("Enter a positive number");
    try {
      await api.patch("/aff-panel/goal", { monthly_goal: v });
      toast.success("Goal updated");
      setEditingGoal(false);
      setGoalInput("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
  };

  const generatePromoCode = async () => {
    try {
      await api.post("/aff-panel/promo-code/generate");
      toast.success("Promo code generated!");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate");
    }
  };

  const share = async (text, url) => {
    if (navigator.share) {
      try { await navigator.share({ title: "Maxlive88", text, url }); return; } catch (_) { /* fall through */ }
    }
    copy(url);
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/aff-panel/dashboard.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `affiliate_dashboard_${data?.promo_code || "snapshot"}_${stamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dashboard PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate PDF");
    } finally { setDownloadingPdf(false); }
  };

  const series = useMemo(() => buildSeries(referrals, 14), [referrals]);
  const totals = data?.totals || {};

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Affiliate Dashboard" subtitle="Loading your premium dashboard…" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          {[0,1,2,3,4,5,6,7].map((i) => (
            <div key={i} className="p-glass p-skeleton" style={{ height: 110 }} />
          ))}
        </div>
        <div className="p-glass p-skeleton" style={{ height: 320 }} />
      </div>
    );
  }

  const t = totals;
  // Pie composition data — wallet-centric
  const pieData = [
    { name: "Lifetime Paid", value: Math.max(0, Number(t.lifetime_paid || 0)) },
    { name: "Wallet Balance", value: Math.max(0, Number(t.wallet_balance ?? data.wallet_balance ?? 0)) },
    { name: "Pending (Next Cycle)", value: Math.max(0, Number(t.pending_commission || 0)) },
  ];
  const pieTotal = pieData.reduce((s, x) => s + x.value, 0) || 1;
  const PIE_COLORS = ["#d4af37", "#22c073", "#a7f3d0"];

  return (
    <div>
      <PageHeader
        title="Affiliate Dashboard"
        eyebrow={`Earning ${data.commission_pct}% on net loss · auto-credited every ${data.period_days} days`}
        subtitle="Real-time view of your players, commissions and wallet."
        right={
          <button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            className="px-4 h-10 sm:h-11 rounded-xl inline-flex items-center gap-2 text-[12px] sm:text-[12.5px] font-bold p-btn-ghost"
            data-testid="download-dashboard-pdf"
            title="Download dashboard as PDF"
          >
            <FileDown size={14} />
            <span>{downloadingPdf ? "Generating…" : "Download PDF"}</span>
          </button>
        }
      />

      {data.payout_blocked && (
        <div className="mb-4 p-glass" style={{ padding: "14px 18px", background: "linear-gradient(135deg, rgba(239,68,68,0.18), rgba(10,16,24,0.85))", border: "1px solid rgba(239,68,68,0.5)" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "rgba(239,68,68,0.22)", color: "#fca5a5" }}>⚠</div>
            <div>
              <div className="text-sm font-bold text-emerald-200">Payouts temporarily blocked on your account</div>
              <div className="text-[12px] text-white/65">{data.payout_block_reason || "Contact affiliate@maxlive88.com for assistance."}</div>
            </div>
          </div>
        </div>
      )}

      {/* Hero strip: promo code (VIP tier removed) */}
      <div className="mb-5">
        {/* Promo code card */}
        <div className="p-glass p-fade-up relative" style={{
          padding: "clamp(18px, 4vw, 24px)",
          background: data.promo_code
            ? "radial-gradient(900px 220px at 0% 0%, rgba(34, 192, 115, 0.22), transparent 65%), linear-gradient(180deg, rgba(15,28,22,0.65), rgba(10,16,24,0.9))"
            : "radial-gradient(900px 220px at 0% 0%, rgba(212,175,55,0.18), transparent 65%), linear-gradient(180deg, rgba(28,22,12,0.6), rgba(10,16,24,0.92))",
          border: data.promo_code ? "1px solid rgba(34, 192, 115, 0.5)" : "1px solid rgba(212,175,55,0.45)",
        }}>
          {data.promo_code ? (
            <>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="p-gold-chip"><Gift size={11} /> Your Promo Code</div>
                {data.is_customized && (
                  <span className="p-pill p-pill-emerald"><Star size={10} /> Customized</span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    <span className="font-mono font-black p-gold-text tracking-[0.04em] leading-none"
                      style={{ fontSize: "clamp(28px, 7vw, 48px)" }}>
                      {data.promo_code}
                    </span>
                    <button onClick={() => copy(data.promo_code)} className="p-2 rounded-lg p-btn-ghost" title="Copy code" data-testid="copy-promo-code">
                      <Copy size={14} />
                    </button>
                  </div>
                  <div className="text-[11px] sm:text-[12px] text-white/65 mt-2 break-all max-w-2xl">{data.referral_link}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => copy(data.referral_link)} className="px-3 sm:px-4 h-10 sm:h-11 rounded-xl text-[12px] sm:text-[12.5px] font-bold inline-flex items-center gap-2 p-btn-ghost" data-testid="copy-referral-link">
                    <Copy size={14} /> <span className="hidden sm:inline">Copy</span> Link
                  </button>
                  <button onClick={() => share(`Join Maxlive88 with my code ${data.promo_code}`, data.referral_link)} className="px-3 sm:px-4 h-10 sm:h-11 rounded-xl text-[12px] sm:text-[12.5px] inline-flex items-center gap-2 p-btn-gold">
                    <Share2 size={14} /> Share
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <span className="p-pill p-pill-amber"><Gift size={10} /> No promo code yet</span>
              </div>
              <h3 className="text-[20px] sm:text-[24px] font-black p-platinum-text mb-1.5 leading-tight">
                Generate your premium promo code
              </h3>
              <p className="text-[12.5px] sm:text-[13.5px] text-white/65 mb-4 max-w-lg">
                Your unique referral identity. Every new player you bring with this code earns you commission for life.
              </p>
              <button
                onClick={generatePromoCode}
                className="px-5 h-12 rounded-xl p-btn-gold inline-flex items-center gap-2 text-[12.5px]"
                data-testid="generate-promo-code"
              >
                <Sparkles size={15} /> Generate My Promo Code
              </button>
              <div className="text-[11px] text-white/45 mt-3">
                You can customize it once afterwards from the <a href="/promo/promo-codes" className="underline" style={{ color: "#22c073" }}>Promo Codes</a> page.
              </div>
            </>
          )}
        </div>
      </div>

      {/* Wallet hero card */}
      <div className="mb-5 p-glass p-fade-up p-fade-up-1" style={{
        padding: "clamp(18px, 4vw, 22px)",
        background: "radial-gradient(900px 220px at 100% 0%, rgba(212,175,55,0.16), transparent 65%), linear-gradient(180deg, rgba(15,30,22,0.7), rgba(8,14,22,0.95))",
        border: "1px solid rgba(34, 192, 115, 0.45)",
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="p-pill p-pill-emerald"><Wallet size={11} /> Wallet Balance</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">auto-credit every {data.period_days}d</span>
            </div>
            <div className="font-black p-gold-text leading-none" style={{ fontSize: "clamp(32px, 8vw, 52px)" }}>
              ৳ {Number(t.wallet_balance ?? data.wallet_balance ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[12px] text-white/55 mt-2">
              Available to withdraw: <b style={{ color: "#34d399" }}>৳ {Number(t.available_for_payout || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
              {Number(t.in_flight_payouts || 0) > 0 && (
                <span className="ml-2 text-white/45">· In-flight: ৳ {Number(t.in_flight_payouts || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              )}
            </div>
          </div>
          <a href="/promo/payouts" className="px-5 h-11 rounded-xl p-btn-gold inline-flex items-center gap-2 text-[12.5px] shrink-0" data-testid="goto-payouts">
            <Banknote size={14} /> Request Withdraw
          </a>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5">
        <StatCard label="Wallet ৳" value={t.wallet_balance ?? data.wallet_balance ?? 0} prefix="৳ " decimals={2} accent="emerald" icon={Wallet} hint="Auto-credited" delay={0} />
        <StatCard label="Total Players" value={extra?.totals?.total_players ?? (t.referrals ?? 0)} accent="gold" icon={Users} hint={`Lifetime`} delay={60} />
        <StatCard label="Active Players" value={extra?.totals?.active_players ?? 0} accent="emerald" icon={Users} hint="Last 30d" delay={120} />
        <StatCard label="FTD Users" value={extra?.totals?.ftd_users ?? 0} accent="amber" icon={BadgeDollarSign} hint="First-time deposits" delay={180} />
        <StatCard label="Total Deposits" value={extra?.totals?.total_deposits ?? (t.deposits ?? 0)} prefix="৳ " decimals={2} accent="blue" icon={BadgeDollarSign} hint="Lifetime" delay={240} />
        <StatCard label="Commission Earned" value={extra?.totals?.total_commission_earned ?? 0} prefix="৳ " decimals={2} accent="gold" icon={Trophy} hint="Lifetime" delay={300} />
        <StatCard label="Pending Commission" value={extra?.totals?.pending_commission ?? (t.pending_commission ?? 0)} prefix="৳ " decimals={2} accent="amber" icon={BadgeDollarSign} hint="Next cycle" delay={360} />
        <StatCard label="Paid Commission" value={extra?.totals?.paid_commission ?? (t.lifetime_paid ?? 0)} prefix="৳ " decimals={2} accent="emerald" icon={Trophy} hint="Wallet credited + paid" delay={420} />
      </div>

      {/* === Live Cycle Preview (Safe Formula) === */}
      {cyclePreview?.cycle && (() => {
        const cyc = cyclePreview.cycle || {};
        const br = cyclePreview.breakdown || {};
        const projected = Number(cyclePreview.projected_final_credit || 0);
        const prevDeficit = Number(cyclePreview.previous_deficit || 0);
        const startDate = cyc.start_iso ? new Date(cyc.start_iso) : null;
        const endDate = cyc.end_iso ? new Date(cyc.end_iso) : null;
        const settleDate = cyc.settle_iso ? new Date(cyc.settle_iso) : null;
        const fmt = (d) => d ? d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—";
        const fmtBD = (d) => d ? d.toLocaleString("en-GB", { timeZone: "Asia/Dhaka", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " BD" : "—";
        const isPositive = projected >= 0;
        const accentColor = isPositive ? "#22c073" : "#ef4444";
        return (
          <div className="p-glass p-fade-up mb-5" data-testid="live-cycle-preview" style={{
            padding: "20px 22px",
            background: `radial-gradient(700px 220px at 0% 0%, ${isPositive ? "rgba(34,192,115,0.18)" : "rgba(239,68,68,0.18)"}, transparent 65%), linear-gradient(180deg, rgba(15,22,30,0.7), rgba(8,14,22,0.95))`,
            border: `1px solid ${isPositive ? "rgba(34,192,115,0.45)" : "rgba(239,68,68,0.45)"}`,
          }}>
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Activity size={15} style={{ color: accentColor }} />
                  <div className="text-sm font-bold text-white">
                    Live Cycle Preview · Cycle {cyc.cycle_type} ({cyc.year}-{String(cyc.month).padStart(2, "0")})
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(212,175,55,0.18)", color: "#d4af37", border: "1px solid rgba(212,175,55,0.45)" }}>
                    LIVE
                  </span>
                </div>
                <div className="text-[11px] text-white/55">
                  {fmt(startDate)} → {fmt(endDate)} · Settles: <span style={{ color: "#d4af37" }}>{fmtBD(settleDate)}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Projected Credit</div>
                <div className="text-[28px] font-black mt-0.5" style={{ color: accentColor }} data-testid="cycle-projected-credit">
                  {isPositive ? "+" : ""}৳ {projected.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
              <div className="p-fade-up" style={{ padding: "12px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Real Deposits</div>
                <div className="text-[16px] font-bold mt-1 text-white">৳ {Number(br.total_real_deposits || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                <div className="text-[10px] text-white/45 mt-0.5">−Gateway fee ({Number(br.gateway_fee_pct || 0)}%)</div>
              </div>
              <div className="p-fade-up" style={{ padding: "12px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Withdrawals</div>
                <div className="text-[16px] font-bold mt-1 text-white">৳ {Number(br.total_withdrawals || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                <div className="text-[10px] text-white/45 mt-0.5">Completed only</div>
              </div>
              <div className="p-fade-up" style={{ padding: "12px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Bonus/Rebate</div>
                <div className="text-[16px] font-bold mt-1 text-white">৳ {Number(br.total_rebates || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</div>
                <div className="text-[10px] text-white/45 mt-0.5">Deducted from base</div>
              </div>
              <div className="p-fade-up" style={{ padding: "12px 14px", background: "rgba(255,255,255,0.03)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.08)" }}>
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Net Loss Base</div>
                <div className="text-[16px] font-bold mt-1" style={{ color: (br.total_net_diff || 0) >= 0 ? "#22c073" : "#ef4444" }}>
                  ৳ {Number(br.total_net_diff || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[10px] text-white/45 mt-0.5">{(br.total_net_diff || 0) >= 0 ? "Player lost (good)" : "Player won (deficit)"}</div>
              </div>
              <div className="p-fade-up" style={{ padding: "12px 14px", background: "rgba(34,192,115,0.08)", borderRadius: 10, border: "1px solid rgba(34,192,115,0.3)" }}>
                <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#22c073" }}>Commission Rate</div>
                <div className="text-[16px] font-bold mt-1" style={{ color: "#22c073" }}>{Number(cyclePreview.commission_pct || 0)}%</div>
                <div className="text-[10px] text-white/45 mt-0.5">{br.contributing_players || 0} active player{(br.contributing_players || 0) === 1 ? "" : "s"}</div>
              </div>
            </div>

            {prevDeficit > 0 && (
              <div className="mb-3" style={{ padding: "10px 14px", background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: 8 }}>
                <span className="text-[12px] text-white/80">
                  ⚠️ Previous Cycle Deficit: <span style={{ color: "#ef4444", fontWeight: 700 }}>৳ {prevDeficit.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span> — এই amount এই cycle এর কমিশন থেকে কাটা যাবে
                </span>
              </div>
            )}

            <div style={{ padding: "10px 14px", background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.28)", borderRadius: 8 }}>
              <div className="text-[11.5px] text-white/75 leading-relaxed" data-testid="cycle-disclaimer">
                <span style={{ color: "#d4af37", fontWeight: 700 }}>ℹ️ Disclaimer: </span>
                {cyclePreview.disclaimer}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Daily/Monthly mini stats */}
      {extra && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          <div className="p-glass p-fade-up" style={{ padding: "16px 18px" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Deposits Today</div>
            <div className="text-[22px] font-black mt-1" style={{ color: "#22c073" }}>৳ {Number(extra.totals.total_deposits_today || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="p-glass p-fade-up p-fade-up-1" style={{ padding: "16px 18px" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Deposits This Month</div>
            <div className="text-[22px] font-black mt-1" style={{ color: "#d4af37" }}>৳ {Number(extra.totals.total_deposits_month || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
          <div className="p-glass p-fade-up p-fade-up-2" style={{ padding: "16px 18px" }}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-bold">Available to Withdraw</div>
            <div className="text-[22px] font-black mt-1" style={{ color: "#34d399" }}>৳ {Number(extra.totals.available_balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      )}

      {/* Daily Activity Chart — last 14 days signups/FTD/deposits */}
      {extra?.daily_series && extra.daily_series.length > 0 && (
        <div className="p-glass p-fade-up mb-5" style={{ padding: "18px 20px" }}>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Activity size={14} style={{ color: "#22c073" }} />
              <div className="text-sm font-bold text-white">Daily Activity · Last 14 Days</div>
            </div>
            <div className="text-[11px] text-white/55">Signups · FTD · Deposits ৳</div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={extra.daily_series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="dSig" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#d4af37" stopOpacity={0.45} /><stop offset="100%" stopColor="#d4af37" stopOpacity={0} /></linearGradient>
                  <linearGradient id="dFtd" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c073" stopOpacity={0.50} /><stop offset="100%" stopColor="#22c073" stopOpacity={0} /></linearGradient>
                  <linearGradient id="dDep" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} /><stop offset="100%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(d) => (d || "").slice(5)} />
                <Tooltip contentStyle={{ background: "#0a1018", border: "1px solid rgba(34,192,115,0.4)", borderRadius: 10, color: "#f5f1e6", fontSize: 12 }} />
                <Area type="monotone" dataKey="signups" stroke="#d4af37" strokeWidth={2} fill="url(#dSig)" name="Signups" />
                <Area type="monotone" dataKey="ftd" stroke="#22c073" strokeWidth={2} fill="url(#dFtd)" name="FTD" />
                <Area type="monotone" dataKey="deposits" stroke="#3b82f6" strokeWidth={2} fill="url(#dDep)" name="Deposits ৳" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Monthly goal + Leaderboard widgets */}
      {(goal || leaderboard) && (
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mb-5">
          {/* Goal progress */}
          {goal && (
            <div className="p-glass p-fade-up" style={{ padding: "18px 20px", background: "radial-gradient(700px 200px at 100% 0%, rgba(212,175,55,0.16), transparent 65%), linear-gradient(180deg, rgba(15,30,22,0.7), rgba(8,14,22,0.95))", border: "1px solid rgba(34,192,115,0.4)" }}>
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Trophy size={14} style={{ color: "#d4af37" }} />
                    <div className="text-sm font-bold text-white">This Month's Earnings Goal</div>
                    {goal.is_suggested && <span className="text-[9.5px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "rgba(212,175,55,0.18)", border: "1px solid rgba(212,175,55,0.5)", color: "#fff7d6" }}>Suggested</span>}
                  </div>
                  <div className="text-[11px] text-white/55">Forecast vs target · auto-credited each cycle</div>
                </div>
                {!editingGoal ? (
                  <button onClick={() => { setEditingGoal(true); setGoalInput(String(goal.monthly_goal)); }} className="text-[11px] underline" style={{ color: "#34d399" }} data-testid="edit-goal">Change goal</button>
                ) : (
                  <div className="flex items-center gap-1">
                    <input value={goalInput} onChange={(e) => setGoalInput(e.target.value)} type="number" placeholder="৳ goal" className="ap-input" style={{ width: 130, padding: "0 10px", height: 32, background: "rgba(0,0,0,0.32)", border: "1px solid rgba(34,192,115,0.5)", borderRadius: 8, color: "#fff", outline: "none", fontSize: 13 }} data-testid="goal-input" />
                    <button onClick={saveGoal} className="h-8 px-2.5 rounded-md text-[11px] font-bold" style={{ background: "rgba(34,192,115,0.28)", border: "1px solid rgba(34,192,115,0.7)", color: "#34d399" }} data-testid="goal-save">Save</button>
                    <button onClick={() => setEditingGoal(false)} className="h-8 px-2 rounded-md text-[11px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)", color: "#fff" }}>×</button>
                  </div>
                )}
              </div>
              <div className="flex items-end justify-between gap-3 mb-2 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-bold mb-0.5">Forecast</div>
                  <div className="font-black p-gold-text leading-none" style={{ fontSize: "clamp(26px, 6vw, 38px)" }}>৳ {Number(goal.month_forecast || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[11px] text-white/55 mt-1">
                    Earned: <b style={{ color: "#34d399" }}>৳ {Number(goal.month_earned || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
                    {" · "}Live: <span style={{ color: "#d4af37" }}>৳ {Number(goal.month_live_preview || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/55 font-bold mb-0.5">Goal</div>
                  <div className="text-[20px] font-black text-white">৳ {Number(goal.monthly_goal || 0).toLocaleString("en-US")}</div>
                  <div className="text-[12px] font-bold mt-0.5" style={{ color: goal.progress_pct >= 100 ? "#34d399" : "#fff7d6" }}>{goal.progress_pct}% reached</div>
                </div>
              </div>
              <div className="rounded-full overflow-hidden" style={{ height: 10, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(34,192,115,0.25)" }}>
                <div className="h-full transition-all" style={{
                  width: `${Math.min(100, goal.progress_pct)}%`,
                  background: goal.progress_pct >= 100
                    ? "linear-gradient(90deg, #34d399, #d4af37)"
                    : "linear-gradient(90deg, #22c073, #34d399 60%, #d4af37)",
                  boxShadow: "0 0 16px rgba(34,192,115,0.55)",
                }} />
              </div>
              {goal.progress_pct >= 100 && (
                <div className="mt-2 text-[12px] font-bold" style={{ color: "#34d399" }}>🎉 Goal reached! You're crushing it this month.</div>
              )}
            </div>
          )}

          {/* Leaderboard mini */}
          {leaderboard && (
            <div className="p-glass p-fade-up p-fade-up-1" style={{ padding: "18px 20px" }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Trophy size={14} style={{ color: "#d4af37" }} />
                  <div className="text-sm font-bold text-white">Top Affiliates · {leaderboard.window_days}d</div>
                </div>
                <a href="/promo/leaderboard" className="text-[10.5px] uppercase tracking-widest font-bold inline-flex items-center gap-1" style={{ color: "#34d399" }}>View all <ArrowUpRight size={11} /></a>
              </div>
              {leaderboard.my_rank ? (
                <div className="mb-3 rounded-lg p-2.5" style={{ background: "rgba(34,192,115,0.10)", border: "1px solid rgba(34,192,115,0.4)" }}>
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="text-white/65">Your rank</span>
                    <span className="font-black" style={{ color: "#34d399" }}>#{leaderboard.my_rank}<span className="text-white/45 font-normal ml-1">of {leaderboard.total_competitors}</span></span>
                  </div>
                </div>
              ) : (
                <div className="mb-3 rounded-lg p-2.5 text-[12px] text-white/55" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)" }}>
                  Refer your first players to enter the leaderboard.
                </div>
              )}
              {leaderboard.top.length === 0 ? (
                <div className="text-[12.5px] text-white/55 py-3 text-center">No competitors yet</div>
              ) : (
                <div className="space-y-1.5">
                  {leaderboard.top.map((r) => (
                    <div key={r.rank} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg" style={{
                      background: r.is_me ? "rgba(34,192,115,0.10)" : "transparent",
                      border: r.is_me ? "1px solid rgba(34,192,115,0.4)" : "1px solid transparent",
                    }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black shrink-0" style={{
                          background: r.rank === 1 ? "linear-gradient(135deg, #fff7d6, #d4af37)"
                            : r.rank === 2 ? "linear-gradient(135deg, #e5e7eb, #9ca3af)"
                            : r.rank === 3 ? "linear-gradient(135deg, #fed7aa, #c2410c)"
                            : "rgba(255,255,255,0.06)",
                          color: r.rank <= 3 ? "#1a1206" : "#cbd5e1",
                        }}>{r.rank}</div>
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-bold text-white truncate">{r.username || "—"} {r.is_me && <span className="text-[9px] uppercase tracking-widest ml-1" style={{ color: "#34d399" }}>(you)</span>}</div>
                          <div className="text-[10px] font-mono" style={{ color: "#d4af37" }}>{r.promo_code || ""}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-[12.5px] font-black" style={{ color: "#34d399" }}>৳ {Number(r.commission || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Promo code performance */}
      {promoStats && promoStats.length > 0 && (
        <div className="p-glass p-fade-up mb-5" style={{ padding: "18px 20px" }}>
          <div className="flex items-center gap-2 mb-3">
            <Gift size={14} style={{ color: "#22c073" }} />
            <div className="text-sm font-bold text-white">Promo Code / Affiliate List · Lifetime</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px] min-w-[1080px]">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest" style={{ color: "rgba(34,192,115,0.85)" }}>
                <th className="py-2 pr-2">Code</th>
                <th className="py-2 pr-2">Register Date</th>
                <th className="py-2 pr-2 text-right">Reg.</th>
                <th className="py-2 pr-2 text-right">FTD</th>
                <th className="py-2 pr-2 text-right">Deposits</th>
                <th className="py-2 pr-2 text-right">Withdraws</th>
                <th className="py-2 pr-2 text-right">Wins</th>
                <th className="py-2 pr-2 text-right">Losses</th>
                <th className="py-2 pr-2 text-right">Now Balance</th>
              </tr></thead>
              <tbody>
                {promoStats.map((p) => (
                  <tr key={p.code} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }} className="text-white hover:bg-emerald-500/5">
                    <td className="py-2 pr-2 font-mono font-bold whitespace-nowrap" style={{ color: "#d4af37" }}>
                      {p.code}
                      {p.is_primary && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded" style={{ background: "rgba(34,192,115,0.18)", color: "#34d399" }}>PRIMARY</span>}
                    </td>
                    <td className="py-2 pr-2 text-white/65 text-[11.5px]">
                      {p.first_register_date ? new Date(p.first_register_date).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                    </td>
                    <td className="py-2 pr-2 text-right font-bold">{p.registrations}</td>
                    <td className="py-2 pr-2 text-right font-bold" style={{ color: "#22c073" }}>{p.ftd_count}</td>
                    <td className="py-2 pr-2 text-right">৳ {Number(p.total_deposits || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="py-2 pr-2 text-right text-rose-300">৳ {Number(p.total_withdrawals || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="py-2 pr-2 text-right" style={{ color: "#6ee7b7" }}>৳ {Number(p.total_wins || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="py-2 pr-2 text-right" style={{ color: "#fda5b1" }}>৳ {Number(p.total_losses || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="py-2 pr-2 text-right font-bold">৳ {Number(p.current_balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts row */}
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-4 mb-5">
        {/* Signups area chart */}
        <div className="p-glass p-fade-up" style={{ padding: "20px 22px" }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 size={14} className="text-emerald-300" />
                <div className="text-sm font-bold text-white">Player Signups</div>
              </div>
              <div className="text-[11px] text-white/50">Last 14 days · daily new players</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/45">Total</div>
              <div className="text-2xl font-black p-gold-text leading-none">
                {series.reduce((s, x) => s + x.signups, 0)}
              </div>
            </div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c073" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#22c073" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.35)" tickLine={false} axisLine={false} fontSize={10} interval={Math.floor(series.length / 7)} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(10,8,16,0.95)",
                    border: "1px solid rgba(34, 192, 115, 0.4)",
                    borderRadius: 10,
                    color: "#f5f1e6",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#a7f3d0", fontWeight: 700 }}
                  cursor={{ stroke: "rgba(34, 192, 115, 0.4)", strokeDasharray: "3 3" }}
                />
                <Area type="monotone" dataKey="signups" stroke="#22c073" strokeWidth={2} fill="url(#goldFill)" activeDot={{ r: 4, fill: "#a7f3d0", stroke: "#22c073", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Earnings composition */}
        <div className="p-glass p-fade-up p-fade-up-1" style={{ padding: "20px 22px" }}>
          <div className="flex items-center gap-2 mb-3">
            <Trophy size={14} className="text-emerald-300" />
            <div className="text-sm font-bold text-white">Earnings Composition</div>
          </div>
          <div className="flex items-center gap-4">
            <div style={{ width: 140, height: 140 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" innerRadius={42} outerRadius={62} paddingAngle={3} stroke="none">
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: "rgba(10,8,16,0.95)",
                      border: "1px solid rgba(34, 192, 115, 0.4)",
                      borderRadius: 10,
                      color: "#f5f1e6",
                      fontSize: 12,
                    }}
                    formatter={(v) => `৳ ${fmtBDT(v)}`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center justify-between gap-2 text-[12px]">
                  <div className="flex items-center gap-2">
                    <span style={{ width: 10, height: 10, borderRadius: 999, background: PIE_COLORS[i], boxShadow: `0 0 10px ${PIE_COLORS[i]}66` }} />
                    <span className="text-white/70">{d.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-bold">৳ {fmtBDT(d.value)}</div>
                    <div className="text-[10px] text-white/45">{((d.value / pieTotal) * 100).toFixed(1)}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent referrals */}
      <div className="p-glass p-fade-up" style={{ padding: "20px 22px" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Users size={14} className="text-emerald-300" />
            <div className="text-sm font-bold text-white">Recent Players</div>
          </div>
          <a href="/promo/referrals" className="text-[11px] text-emerald-300 hover:text-emerald-200 uppercase tracking-[0.22em] font-bold inline-flex items-center gap-1">
            View all <ArrowUpRight size={11} />
          </a>
        </div>
        {data.recent_referrals?.length ? (
          <div className="divide-y" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
            {data.recent_referrals.map((r, i) => (
              <div key={i} className="py-3 flex items-center justify-between" style={{ borderTop: i ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-[12px] font-black" style={{
                    background: "linear-gradient(135deg, #fff7d6, #d4af37 35%, #22c073 100%)",
                    color: "#1a1206",
                    boxShadow: "0 6px 18px rgba(34, 192, 115, 0.3)",
                  }}>
                    {(r.username || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">{r.username || "—"}</div>
                    <div className="text-[11px] text-white/45">Joined {shortDate(r.joined_at)}</div>
                  </div>
                </div>
                <span className="p-pill p-pill-emerald">Active</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="inline-flex w-16 h-16 rounded-full items-center justify-center mb-3" style={{
              background: "linear-gradient(135deg, rgba(34, 192, 115, 0.18), rgba(34, 192, 115, 0.02))",
              border: "1px solid rgba(34, 192, 115, 0.4)",
            }}>
              <Users size={26} className="text-emerald-300" />
            </div>
            <div className="text-white/65 text-sm font-semibold">No players yet</div>
            <div className="text-white/45 text-[12px] mt-1">Share your promo code to start earning</div>
          </div>
        )}
      </div>
    </div>
  );
}
